import { useCallback, useEffect, useState } from 'react'
import { eachLimit } from 'async-es'
import Storage from '@st-operations/uikit/Storage'

import {
  CONTENT_RULES,
  removeDuplicatesBasedOnSize,
  isSameTopic,
  extractDemographics,
} from '../utils'

import { searchTopic, getContentRules, rescale } from '_services/audience'
import KEYS from '_constants/keys'
import { logErrorAsync } from '../../../lib/utility'

const TOPIC_KEYWORD_EXPANSION_METHOD_DEFAULT = 'v1'

const TOPICS_INITIAL_STATE = {
  recommendedTopics: [],
  similarTopics: [],
  signalTopics: [],
}

const SELECTED_TOPIC_INITIAL_STATE = {
  name: '',
  _id: '',
}

export function replaceAt(array, index, element) {
  if (index < 0) {
    return [...array]
  }
  return [...array.slice(0, index), element, ...array.slice(index + 1)]
}

const useSelectedTopics = ({ audience, setAudience, isEU }) => {
  const [topicsSearch, setTopicsSearch] = useState('')
  const [scale, setScale] = useState(+Storage.get('ac_topic_search_scale') || 1)
  const [scaleDirection, setScaleDirection] = useState('up')
  const [selectedTopic, setSelectedTopic] = useState(SELECTED_TOPIC_INITIAL_STATE)
  const [selectedTopicForKeywordModal, setSelectedTopicForKeywordModal] = useState()
  const [topics, setTopics] = useState(TOPICS_INITIAL_STATE)
  const [isLoading, setIsLoading] = useState(false)
  const [keywords, setKeywords] = useState({})
  const [canSearch, setCanSearch] = useState(true)
  const [preBuiltAttrTypes, setPreBuiltAttrTypes] = useState([])

  const topicAlreadyAdded = (topic) =>
    audience?.topics?.some(
      (audienceTopic) =>
        audienceTopic?.name?.toLowerCase() === topic?.toLowerCase() ||
        audienceTopic?.origName?.toLowerCase() === topic?.toLowerCase()
    )

  const handleSearchTopicsInputChange = (value) => {
    setTopicsSearch(value)
  }

  const handleTopicsKeyDown = async (event) => {
    let newValue = topicsSearch
    if (event.which === KEYS.ENTER) {
      await searchTopics(newValue.trim().replace(/,/g, ''))
      setTopicsSearch('')
    }
  }

  const handleClearTopicSelection = () => {
    setSelectedTopic(SELECTED_TOPIC_INITIAL_STATE)
    setTopics(TOPICS_INITIAL_STATE)
    setAudience((prevState) => ({
      ...prevState,
      topics: prevState.topics.filter((f) => f.atoms.seeds),
    }))
  }
  const handleRescaleTopics = async () => {
    const { topics: currentTopics } = audience
    setIsLoading(true)
    try {
      const topics = await rescale({
        scaleDirection,
        version: TOPIC_KEYWORD_EXPANSION_METHOD_DEFAULT,
        scale,
        topics: currentTopics,
        languages: audience.languages.map(({ value }) => value).join(','),
        ...(audience?.regions?.length > 0 && {
          regions: audience.regions,
        }),
        ...(audience?.destinations?.length > 0 && {
          destinations: audience.destinations.map(({ _id }) => _id).join(','),
        }),
        ...extractDemographics(audience),
        afs_brands: [
          ...(audience?.afsBrand || []),
          ...(audience?.afsCategory || []),
          ...(audience?.afsLifestyle || []),
          ...(audience?.afsLiveramp || []),
        ],
      })
      setAudience((prevState) => ({
        ...prevState,
        topics,
      }))
      toast('Rescaled topics successfully')
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      toast.error('Error while rescaling topics')
    }
  }

  const handleRemoveTopic = (topic) => () => {
    const newTopics = audience.topics.filter((audienceTopic) => !isSameTopic(audienceTopic, topic))

    setAudience((prevState) => {
      let newAudience = { ...prevState, topics: newTopics }
      if (!newTopics.find((f) => f.atoms.seeds)) {
        newAudience = {
          ...newAudience,
          threshold: {},
          seed: {},
        }
      }
      return newAudience
    })

    if (isSameTopic(selectedTopic, topic)) {
      setSelectedTopic(SELECTED_TOPIC_INITIAL_STATE)
      setTopics(TOPICS_INITIAL_STATE)
    } else {
      setTopics((prev) => ({
        ...prev,
        ...(topic.ref === 'similar' && {
          similarTopics: [...prev.similarTopics, topic],
        }),
        ...(topic.ref === 'recommended' && {
          recommendedTopics: [...prev.recommendedTopics, topic],
        }),
        ...(topic.ref === 'signal' && {
          signalTopics: [...prev.signalTopics, topic],
        }),
      }))
    }
  }

  const removeTopicFromLists = (topic) => {
    setTopics((prev) => ({
      ...prev,
      similarTopics: prev.similarTopics.filter((prevTopic) => prevTopic?.name !== topic?.name),
      recommendedTopics: prev.recommendedTopics.filter(
        (prevTopic) => prevTopic?._id !== topic?._id
      ),
      signalTopics: prev.signalTopics.filter((prevTopic) => prevTopic?.name !== topic?.name),
    }))
  }

  const handleAddTopic = (topic) => {
    if (topicAlreadyAdded(topic.name)) {
      toast.error(`Topic "${topic.name}" had already been added`)
      return
    }

    removeTopicFromLists(topic)
    setAudience((prev) => ({ ...prev, topics: [...prev.topics, topic] }))
  }

  const handleEditTopicKeywords = (keywords) => {
    setAudience((prev) => {
      const prevTopicIndex = prev.topics.findIndex((f) =>
        isSameTopic(f, selectedTopicForKeywordModal)
      )
      const removedKeywords = prev.topics[prevTopicIndex].atoms.keywords.filter(
        (prevKeyword) => !keywords.find((currKeyword) => currKeyword === prevKeyword)
      )
      const topics = prev.topics.map((topic) => ({
        ...topic,
        atoms: {
          ...topic.atoms,
          keywords:
            topic.atoms?.keywords?.filter(
              (currKeyword) => !removedKeywords.includes(currKeyword)
            ) || [],
        },
      }))
      const newAudience = {
        ...prev,
        topics,
        // blocklist: [...new Set([...(audience.blocklist || []), ...removedKeywords])],
      }

      return newAudience
    })
  }

  const handleAddMultipleTopics = (topics) => {
    const filteredTopics = topics.filter((topic) => topic.name && !topicAlreadyAdded(topic.name))
    setAudience((prev) => ({ ...prev, topics: [...prev.topics, ...filteredTopics] }))
  }

  const handleSelectTopic = (topic) => () => {
    const topicName = topic.origName ?? topic.name
    setTopics(TOPICS_INITIAL_STATE)
    setSelectedTopic(topic)
    searchTopics(topicName)
  }

  const handleRefreshTopics = () => {
    if (selectedTopic?._id || selectedTopic?.name) {
      searchTopics(selectedTopic.name)
    }
  }

  const handleScaleChange = (newValue) => {
    setScale((prevVal) => {
      const cureVal = +newValue
      if (+prevVal <= cureVal) setScaleDirection('up')
      else setScaleDirection('down')
      return cureVal
    })
    Storage.set('ac_topic_search_scale', `${newValue}`)
  }

  const searchTopics = useCallback(
    async (query) => {
      if (!isLoading && canSearch) {
        setIsLoading(true)

        try {
          const response = await searchTopic({
            query,
            language: audience.languages.map(({ value }) => value).join(','),
            vertical: audience.vertical,
            ...(audience?.regions?.length > 0 && {
              regions: audience.regions.map(({ _id }) => _id).join(','),
            }),
            ...(audience?.destinations?.length > 0 && {
              destinations: audience.destinations.map(({ _id }) => _id).join(','),
            }),
            modifier: audience.topicSearchModifier?.toLowerCase() || 'similar',
            version: TOPIC_KEYWORD_EXPANSION_METHOD_DEFAULT,
            scale: Math.min(scale, 5),
            // blocklist: audience.blocklist || [],
            geo_audience: audience?.geoAudience,
            kecc_type: audience?.keccType?.toLowerCase(),
            ...extractDemographics(audience),
            afs_brands: [
              ...(audience?.afsBrand || []),
              ...(audience?.afsCategory || []),
              ...(audience?.afsLifestyle || []),
              ...(audience?.afsLiveramp || []),
            ],
          })

          const {
            topic,
            recommendedTopics = [],
            similarTopics = [],
            signalTopics = [],
            translatedTopics = [],
          } = response

          const [
            filteredRecommended,
            filteredSimilar,
            filteredSignal,
          ] = removeDuplicatesBasedOnSize(topic, recommendedTopics, similarTopics, signalTopics)

          setSelectedTopic(topic)
          setTopics({
            recommendedTopics: filteredRecommended?.map((item) => ({
              ...item,
              ref: 'recommended',
            })),
            similarTopics: filteredSimilar?.map((item) => ({
              ...item,
              ref: 'similar',
            })),
            signalTopics: filteredSignal?.map((item) => ({
              ...item,
              ref: 'signal',
            })),
            translatedTopics: translatedTopics?.map((item) => ({
              ...item,
              ref: 'signal',
            })),
          })

          if (!topicAlreadyAdded(query)) {
            setAudience((prev) => ({
              ...prev,
              topics: [...prev.topics, topic],
            }))
          }

          setIsLoading(false)
        } catch (error) {
          logErrorAsync(error)
          setIsLoading(false)
          setTopics(TOPICS_INITIAL_STATE)
          toast.error(error.message)
        }
      }
    },
    [audience, canSearch, scale, isLoading]
  )

  const handleAddOfflineTopics = (offlineTopics) => {
    const currentTopic = audience?.topics || []
    //removing offline topics to replace new ones
    const filteredTopic = currentTopic.filter((topic) => !preBuiltAttrTypes.includes(topic.type))

    const newTopics = offlineTopics.map(({ type, topicName, atoms, predictorScore = null }) => ({
      type,
      atoms: atoms || {
        keywords: [
          type === 'psychographics' ? topicName?.replace('psychographics', 'ls') : topicName,
        ],
      },
      name: topicName,
      sizes: {},
      ...(predictorScore && { predictorScore }),
    }))

    setTopics(TOPICS_INITIAL_STATE)
    setAudience((prev) => ({
      ...prev,
      topics: [...filteredTopic, ...newTopics],
    }))
  }

  const loadContentRules = () => {
    eachLimit(CONTENT_RULES, 3, async (item, next) => {
      try {
        const response = await getContentRules(item?._id)
        setKeywords((prev) => ({
          ...prev,
          [item.name]: response.tokens.split('\n').map((item) => item.toLowerCase()),
        }))
        next()
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    })
  }

  useEffect(() => {
    loadContentRules()
  }, [])

  useEffect(() => {
    if (keywords.sensitiveKeywords) {
      const { sensitiveKeywords, inappropriateKeywords, sensitiveKeywordsEu } = keywords
      let isAbleToSearch =
        !sensitiveKeywords.includes(topicsSearch.toLowerCase().trim()) &&
        !inappropriateKeywords.includes(topicsSearch.toLowerCase().trim())
      if (isEU) {
        isAbleToSearch =
          isAbleToSearch && !sensitiveKeywordsEu.includes(topicsSearch.toLowerCase().trim())
      }

      setCanSearch(isAbleToSearch)
    }
  }, [keywords, topicsSearch, isEU])

  return {
    topics,
    selectedTopic,
    selectedTopicForKeywordModal,
    topicsSearch,
    isLoading,
    preBuiltAttrTypes,
    scale,
    handleAddOfflineTopics,
    handleClearTopicSelection,
    handleSelectTopic,
    handleRemoveTopic,
    handleAddTopic,
    handleEditTopicKeywords,
    handleAddMultipleTopics,
    handleSearchTopicsInputChange,
    handleTopicsKeyDown,
    handleRefreshTopics,
    handleRescaleTopics,
    handleScaleChange,
    setSelectedTopicForKeywordModal,
    setPreBuiltAttrTypes,
    // searchTopics,
    // canSearch,
  }
}

export default useSelectedTopics
